import { Box, Card, HStack, Icon, Image, Text, VStack } from "@chakra-ui/react";
import { getToken, getTools, Route, TokenExtended } from "@lifi/sdk";
import {
  ChainTokenSelected,
  LiFiWidget,
  RouteSelected,
  useWidgetEvents,
  WidgetConfig,
  WidgetEvent,
  WidgetTheme,
} from "@lifi/widget";
import { useAppKit } from "@reown/appkit/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import { useSearchParams } from "react-router";
import { useBalance } from "wagmi";

import { SmartLink } from "@/components/smart-link";
import { ARBITRUM_BRIDGE_URL, PUSD_URL } from "@/constants/shared";
import { viemChains } from "@/data/chains";
import { useAuth } from "@/hooks/useAuth";
import { useWalletBalanceInfo } from "@/hooks/useWalletBalancesInfo";
import { getIconPathFromAppId } from "@/utils";

const widgetTheme: WidgetTheme = {
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: "#111111",
        },
        secondary: {
          main: "#747474",
        },
        background: {
          default: "#ffffff",
          paper: "#f3f5f9",
        },
        text: {
          primary: "#00070F",
          secondary: "#6A7481",
        },
        grey: {
          200: "#EEEFF2",
          300: "#D5DAE1",
          700: "#555B62",
          800: "#373F48",
        },
        playground: {
          main: "#f3f5f8",
        },
      },
    },
  },
  container: {
    border: "none",
    borderRadius: "16px",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "32px",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: "32px",
        },
      },
    },
    MuiCard: {
      defaultProps: { variant: "filled" },
    },
  },
};

const STABLECOIN_SYMBOLS = [
  "USDT",
  "USDC",
  "DAI",
  "TUSD",
  "BUSD",
  "GUSD",
  "USDP",
  "USDD",
  "USDS",
  "sUSD",
  "LUSD",
  "FRAX",
  "MIM",
  "alUSD",
  "USDN",
  "XUSD",
  "mUSD",
  "nUSD",
  "CUSD",
  "EUSD",
  "HUSD",
  "PAX",
  "USDX",
  "USDCe",
];

const PUSD_ADDRESS = "0xdddd73f5df1f0dc31373357beac77545dc5a6f3f";
const isNonPusdStablecoin = (token: TokenExtended | undefined) => {
  if (!token) return false;
  return STABLECOIN_SYMBOLS.includes(token.symbol) && token.symbol !== "pUSD";
};

export const BridgeWidget = () => {
  const { data: tools } = useQuery({
    queryKey: ["lifi-widget-tools"],
    queryFn: () => getTools(),
  });
  const { exchanges } = tools ?? { exchanges: [] };
  const { address } = useAuth();
  const { open } = useAppKit();

  const { refetch } = useBalance({
    address: address ?? undefined,
  });
  const { refetchBalance } = useWalletBalanceInfo(address ?? "");
  const [highValueLoss, setHighValueLoss] = useState(false);
  const [destinationChainTokenFromWidget, setDestinationChainTokenFromWidget] =
    useState<ChainTokenSelected | undefined>();
  const [sourceChainTokenFromWidget, setSourceChainTokenFromWidget] = useState<
    ChainTokenSelected | undefined
  >();

  const [searchParams] = useSearchParams();
  const fromChainFromParams = searchParams.get("fromChain");
  const fromTokenFromParams = searchParams.get("fromToken");
  const toChainFromParams = searchParams.get("toChain");
  const toTokenFromParams = searchParams.get("toToken");

  const sourceChainTokenFromParams = {
    chainId: fromChainFromParams
      ? parseInt(fromChainFromParams, 10)
      : undefined,
    tokenAddress: fromTokenFromParams ? fromTokenFromParams : undefined,
  };

  const destinationChainTokenFromParams = {
    chainId: toChainFromParams ? parseInt(toChainFromParams, 10) : undefined,
    tokenAddress: toTokenFromParams ? toTokenFromParams : undefined,
  };

  const sourceChainToken =
    sourceChainTokenFromWidget ?? sourceChainTokenFromParams;
  const destinationChainToken =
    destinationChainTokenFromWidget ?? destinationChainTokenFromParams;

  const { data: sourceToken } = useQuery({
    queryKey: [
      "sourceToken",
      sourceChainToken?.chainId,
      sourceChainToken?.tokenAddress,
    ],
    queryFn: () => {
      if (!sourceChainToken?.chainId || !sourceChainToken?.tokenAddress)
        return undefined;
      return getToken(
        sourceChainToken?.chainId,
        sourceChainToken?.tokenAddress,
      );
    },
  });

  const { data: destinationToken } = useQuery({
    queryKey: [
      "destinationToken",
      destinationChainToken?.chainId,
      destinationChainToken?.tokenAddress,
    ],
    queryFn: () => {
      if (
        !destinationChainToken?.chainId ||
        !destinationChainToken?.tokenAddress
      )
        return undefined;
      return getToken(
        destinationChainToken?.chainId,
        destinationChainToken?.tokenAddress,
      );
    },
  });
  const isTransferringUsd = isNonPusdStablecoin(destinationToken);

  // Check if transferring PLUME token
  const isTransferringPlume = sourceToken && sourceToken.symbol === "PLUME";
  // Check if cross-chain transfer
  const isCrossChain =
    sourceChainToken?.chainId &&
    destinationChainToken?.chainId &&
    sourceChainToken.chainId !== destinationChainToken.chainId;
  const shouldShowArbitrumCTA = isTransferringPlume && isCrossChain;

  const widgetEvents = useWidgetEvents();
  useEffect(() => {
    const onRouteExecutionCompleted = () => {
      refetch();
      refetchBalance();
      setHighValueLoss(false);
    };

    const onSourceChainTokenSelected = (e: ChainTokenSelected) => {
      setSourceChainTokenFromWidget(e);
      setHighValueLoss(false);
    };

    const onDestinationChainTokenSelected = (e: ChainTokenSelected) => {
      setDestinationChainTokenFromWidget(e);
      setHighValueLoss(false);
    };
    const onRouteSelected = (e: RouteSelected) => {
      if (Number(e.route.fromAmountUSD) <= 0) return;
      if (Number(e.route.toAmountUSD) / Number(e.route.fromAmountUSD) < 0.985) {
        setHighValueLoss(true);
      } else {
        setHighValueLoss(false);
      }
      // Extract source chain/token from route as fallback
      if (e.route.fromToken && e.route.fromChainId) {
        setSourceChainTokenFromWidget({
          chainId: e.route.fromChainId,
          tokenAddress: e.route.fromToken.address,
        });
      }
    };

    widgetEvents.on(
      WidgetEvent.RouteExecutionCompleted,
      onRouteExecutionCompleted,
    );
    widgetEvents.on(
      WidgetEvent.SourceChainTokenSelected,
      onSourceChainTokenSelected,
    );
    widgetEvents.on(
      WidgetEvent.DestinationChainTokenSelected,
      onDestinationChainTokenSelected,
    );
    widgetEvents.on(WidgetEvent.RouteSelected, onRouteSelected);
    widgetEvents.on(WidgetEvent.AvailableRoutes, (e: Route[]) => {
      const bestRoute = e.sort(
        (a, b) => Number(b.toAmountUSD) - Number(a.toAmountUSD),
      )[0];
      setHighValueLoss(
        Number(bestRoute.fromAmountUSD) > 0 &&
          Number(bestRoute.toAmountUSD) / Number(bestRoute.fromAmountUSD) <
            0.985,
      );
      // Extract source chain/token from best route as fallback
      if (bestRoute?.fromToken && bestRoute?.fromChainId) {
        setSourceChainTokenFromWidget({
          chainId: bestRoute.fromChainId,
          tokenAddress: bestRoute.fromToken.address,
        });
      }
    });
    widgetEvents.on(WidgetEvent.TokenSearch, () => setHighValueLoss(false));
    return () => widgetEvents.all.clear();
  }, [widgetEvents, refetch, refetchBalance]);

  const config: Omit<WidgetConfig, "integrator"> = {
    appearance: "light",
    slippage: 0.005,
    buildUrl: true,
    tokens: {
      from: {
        deny: [
          {
            chainId: 1,
            address: PUSD_ADDRESS,
          },
        ],
      },
      to: {
        deny: [
          {
            chainId: 1,
            address: PUSD_ADDRESS,
          },
        ],
      },
    },
    exchanges: { deny: exchanges.map((e) => e.key) },
    sdkConfig: {
      rpcUrls: viemChains.reduce(
        (acc: Partial<Record<number, string[]>>, chain) => {
          acc[chain.id] = chain.rpcUrls.default.http;
          return acc;
        },
        {} as Partial<Record<number, string[]>>,
      ),
    },
    walletConfig: {
      onConnect: () => {
        open();
      },
    },
    theme: {
      ...widgetTheme,
    },
  };

  return (
    <Box
      w="fit-content"
      h="fit-content"
      mx="auto"
      bg="bg"
      rounded="2xl"
      boxShadow="0 16px 35px 0 rgba(0, 0, 0, 0.05);"
    >
      <LiFiWidget config={config} integrator="Plume" />
      {shouldShowArbitrumCTA && (
        <CTA
          title="Bridging PLUME across chains?"
          description="Use the Arbitrum Bridge for the best rates when bridging large amounts of PLUME"
          bg="linear-gradient(1deg, #353B58 0.89%, #17171A 98.95%)"
          action="arbitrum"
        />
      )}
      {!shouldShowArbitrumCTA && isTransferringUsd && (
        <CTA
          title="Bridging in stablecoins?"
          description="pUSD is Plume's native stablecoin, consider minting it with USD to avoid hassle and fees"
          bg="linear-gradient(1deg, #353B58 0.89%, #17171A 98.95%)"
        />
      )}
      {!shouldShowArbitrumCTA && !isTransferringUsd && highValueLoss && (
        <CTA
          title="This route has a large price impact"
          description="Consider minting pUSD with USDC for a fast and zero fee way to get funds into Plume!"
          bg="linear-gradient(5deg, #483028 4.11%, #1A1817 95.99%);"
        />
      )}
    </Box>
  );
};

const CTA = ({
  title,
  description,
  bg,
  action = "pusd",
}: {
  title: string;
  description: string;
  bg: string;
  action?: "pusd" | "arbitrum";
}) => {
  const isArbitrum = action === "arbitrum";
  const href = isArbitrum ? ARBITRUM_BRIDGE_URL : PUSD_URL;
  const iconSrc = isArbitrum
    ? getIconPathFromAppId("arb_native_bridge")
    : "/assets/tokens/pusd.svg";
  const buttonText = isArbitrum ? "Arbitrum Bridge" : "Mint pUSD";

  return (
    <Box w="416px" px={6} pb={5}>
      <Card.Root
        rounded="xl"
        background={bg}
        px={4}
        py={3}
        w="full"
        alignSelf="center"
      >
        <VStack align="flex-start" gap={0} w="full">
          <Text fontSize="sm" fontWeight="500" color="fg.inverted" mb={1}>
            {title}
          </Text>

          <Text
            mb={3}
            fontSize="sm"
            color="fg.inverted"
            opacity="0.6"
            fontWeight="500"
          >
            {description}
          </Text>
          <SmartLink
            href={href}
            display="inline-flex"
            alignItems="center"
            borderRadius="full"
            bg="rgba(255, 255, 255, 0.1)"
            color="white"
            _hover={{ bg: "rgba(255, 255, 255, 0.15)" }}
            px={4}
            py={2}
            w="full"
            h={9}
            textDecoration="none"
            transition="background-color 0.2s"
          >
            <HStack gap={2} justifyContent="center" w="full">
              <Image src={iconSrc} boxSize={5} rounded="full" />
              <Text fontSize="sm" fontWeight="500">
                {buttonText}
              </Text>
              <Icon boxSize={4}>
                <FiArrowUpRight />
              </Icon>
            </HStack>
          </SmartLink>
        </VStack>
      </Card.Root>
    </Box>
  );
};
